import { cx } from "@suse-o11y/class-variance";
import { IconCaretDown, IconQuestionCircleOutlined } from "@suse-o11y/icons";
import { isString } from "@suse-o11y/is-string";
import { isTruthy } from "@suse-o11y/is-truthy";
import { Tooltip, TooltipBoundary, TooltipTrigger } from "@suse-o11y/tooltip";
import React from "react";

import { CollapsibleTrigger } from "~/components/collapsible/trigger";
import { Truncated } from "~/components/truncated";

import { Wrap } from "../conditional-wrapper/Wrap";

import styles from "./widget.module.css";
import { useIsWidgetExpandable } from "./widget-context";

interface WidgetHeaderRegularProps {
	title?: React.ReactNode;

	description?: React.ReactNode;

	/**
	 * Size of the title.
	 *
	 * @default "md"
	 */
	size?: "sm" | "md" | "lg" | "xl";

	/**
	 * Display a help (?) icon when not empty.
	 *
	 * @default ""
	 */
	help?: string;

	truncate?: never;

	className?: string;
}

interface WidgetHeaderTruncatedProps extends Omit<WidgetHeaderRegularProps, "title" | "truncate"> {
	title: string;

	/**
	 * Whether to truncate the title.
	 *
	 * @default true
	 */
	truncate?: boolean;
}

export type WidgetHeaderProps = WidgetHeaderRegularProps | WidgetHeaderTruncatedProps;

export const WidgetHeader: React.FC<React.PropsWithChildren<WidgetHeaderProps>> = ({
	title,
	description,
	size = "md",
	help = "",
	truncate = true,
	className,
	children,
	...props
}) => {
	const expandable = useIsWidgetExpandable();

	const expandElement = expandable && (
		<button className="button button-sm button-square -mx-1.5" type="button">
			<IconCaretDown className={styles.headerExpandIcon} height="8" width="12" />
		</button>
	);

	const titleElement = isTruthy(title) && (
		<WidgetHeaderTitle
			className={cx(
				styles.headerTitle,
				size === "sm" && styles.headerTitleSizeSm,
				size === "md" && styles.headerTitleSizeMd,
				size === "lg" && styles.headerTitleSizeLg,
				size === "xl" && styles.headerTitleSizeXl,
			)}
			truncate={truncate}
		>
			{title}
		</WidgetHeaderTitle>
	);

	const helpElement = help.length > 0 && (
		<TooltipBoundary>
			<TooltipTrigger aria-label="More information" className="button button-sm button-square not-first:ml-1">
				<IconQuestionCircleOutlined className="text-core-icon-neutral-tertiary" />
			</TooltipTrigger>

			<Tooltip className="tooltip/tooltip">{help}</Tooltip>
		</TooltipBoundary>
	);

	const headerLeft =
		expandElement || titleElement || helpElement ? (
			<div className="grid grid-flow-col items-center">
				{expandElement}
				{titleElement}
				{helpElement}
			</div>
		) : null;

	if (!(headerLeft || children || description)) {
		return null;
	}

	return (
		<Wrap if={expandable} with={<CollapsibleTrigger asChild className={styles.headerExpandable} role="button" />}>
			<header className={cx(expandable && "cursor-pointer", className, styles.header)} {...props}>
				{headerLeft}

				{/* do not propagate the click on expander */}
				{children && (
					<div className="contents select-text" onClick={(event) => event.stopPropagation()}>
						{children}
					</div>
				)}

				{description && <div className="col-span-full w-full justify-self-start">{description}</div>}
			</header>
		</Wrap>
	);
};

const WidgetHeaderTitle: React.FC<
	React.PropsWithChildren<{
		className?: string;
		truncate: boolean;
	}>
> = ({ truncate, children, ...props }) => {
	if (truncate && isString(children)) {
		return <Truncated {...props}>{children}</Truncated>;
	}

	return <div {...props}>{children}</div>;
};
